import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Printer, Download, FileText, FileSpreadsheet, FileType,
  ChevronDown, ChevronUp, BookOpen, Calculator, CalendarDays,
  GitCompareArrows, Info, ArrowLeft, History, ChevronsUpDown
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { PM: 'Project Manager', CS: 'Consultant', CF: 'Config Specialist', IN: 'Integrations', DM: 'Data Migration', DE: 'Developer', IF: 'Infrastructure' };

// ── Collapsible section ─────────────────────────────────────
function Section({ id, title, icon: Icon, children, defaultOpen = true, forceOpen }) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = forceOpen !== undefined ? forceOpen : localOpen;
  const toggle = () => { if (forceOpen === undefined) setLocalOpen(!localOpen); };
  return (
    <Card className="print:shadow-none print:border print:break-inside-avoid" data-testid={`math-section-${id}`}>
      <CardHeader className="pb-2 cursor-pointer select-none" onClick={toggle}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
            {Icon && <Icon className="w-4.5 h-4.5 text-[#4ABFCF]" />}
            {title}
          </CardTitle>
          <button className="text-slate-400 hover:text-slate-600 print:hidden">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </CardHeader>
      {open && <CardContent className="pt-0 space-y-5">{children}</CardContent>}
    </Card>
  );
}

function Formula({ lines }) {
  return (
    <div className="bg-white rounded-md px-4 py-3 font-mono text-[11px] border border-slate-200 space-y-0.5 print:bg-slate-50 print:text-[10px]">
      {lines.map((l, i) => <div key={`mref-line-${i}-${l.slice(0, 20)}`}>{l}</div>)}
    </div>
  );
}

function LiveValue({ label, value, highlight }) {
  return (
    <div className={`flex justify-between items-baseline text-xs ${highlight ? 'font-semibold border-t border-slate-200 pt-1.5 mt-1.5 text-[#0F2040]' : 'text-slate-600'}`}>
      <span>{label}</span>
      <span className={`font-mono tabular-nums ${highlight ? 'text-[#4ABFCF]' : ''}`}>{value}</span>
    </div>
  );
}

function SubHeading({ children }) {
  return <h4 className="font-semibold text-[#0F2040] text-sm">{children}</h4>;
}

function Paragraph({ children }) {
  return <p className="text-xs text-slate-500 leading-relaxed">{children}</p>;
}

// ════════════════════════════════════════════════════════════
export default function MathReferencePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const estimateId = searchParams.get('estimate_id');

  const [estimates, setEstimates] = useState([]);
  const [selectedId, setSelectedId] = useState(estimateId || '');
  const [liveData, setLiveData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const contentRef = useRef(null);
  const [changelog, setChangelog] = useState([]);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState(undefined); // undefined = individual control

  // Fetch estimates list
  useEffect(() => {
    axios.get(`${API_URL}/api/estimates`, { withCredentials: true })
      .then(r => setEstimates(r.data?.estimates || r.data || []))
      .catch(() => {});
  }, []);

  // Fetch config changelog
  useEffect(() => {
    if (!changelogOpen) return;
    axios.get(`${API_URL}/api/planner/config-changelog?limit=5`, { withCredentials: true })
      .then(r => setChangelog(r.data?.entries || []))
      .catch(() => {});
  }, [changelogOpen]);

  // Fetch live data when an estimate is selected
  useEffect(() => {
    if (!selectedId) { setLiveData(null); return; }
    const fetchLive = async () => {
      try {
        const [estRes, tlRes] = await Promise.all([
          axios.get(`${API_URL}/api/estimates/${selectedId}`, { withCredentials: true }),
          axios.post(`${API_URL}/api/planner/timeline`, { estimate_id: selectedId }, { withCredentials: true }),
        ]);
        setLiveData({ estimate: estRes.data, timeline: tlRes.data });
      } catch {
        setLiveData(null);
      }
    };
    fetchLive();
  }, [selectedId]);

  const est = liveData?.estimate;
  const res = est?.results;
  const tl = liveData?.timeline;
  const mods = est?.modifiers || {};

  const handlePrint = () => window.print();

  const handleExport = async (format) => {
    setExportOpen(false);
    try {
      const resp = await axios.post(
        `${API_URL}/api/math-reference/export/${format}`,
        { estimate_id: selectedId || null },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = { pdf: 'pdf', docx: 'docx', xlsx: 'xlsx' }[format] || format;
      a.download = `Math_Reference${selectedId ? '_' + (est?.estimate_name || '').replace(/\s/g, '_').slice(0, 20) : ''}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch {
      toast.error(`Export failed`);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 print:max-w-none print:px-8" data-testid="math-reference-page">
      {/* Header */}
      <div className="mb-6 print:mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-[#0F2040] flex items-center gap-2" data-testid="math-ref-title">
              <BookOpen className="w-5 h-5 text-[#4ABFCF]" />
              Calculation Reference
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0 print:hidden">
          {/* Estimate selector */}
          <Select value={selectedId || 'none'} onValueChange={(v) => setSelectedId(v === 'none' ? '' : v)}>
            <SelectTrigger className="w-[220px] text-xs" data-testid="math-ref-estimate-select">
              <SelectValue placeholder="Select estimate for live values" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No estimate (static)</SelectItem>
              {estimates.map(e => (
                <SelectItem key={e.id} value={e.id}>
                  {e.estimate_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="math-ref-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>

          <Popover open={exportOpen} onOpenChange={setExportOpen}>
            <PopoverTrigger asChild>
              <Button className="bg-[#0F2040] hover:bg-[#1A3055] text-white" size="sm" data-testid="math-ref-export-btn">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-48 p-0">
              <div className="px-3 py-2 border-b"><p className="text-sm font-semibold text-[#0F2040]">Export Format</p></div>
              <div className="py-1">
                <button onClick={() => handleExport('pdf')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="math-export-pdf">
                  <FileText className="w-4 h-4 text-red-500" /> PDF
                </button>
                <button onClick={() => handleExport('docx')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="math-export-docx">
                  <FileType className="w-4 h-4 text-blue-500" /> Word
                </button>
                <button onClick={() => handleExport('xlsx')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="math-export-xlsx">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-500" /> Excel
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        </div>

        {/* Full-width subtitle + Expand/Collapse toggle */}
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-sm text-slate-500">
            Complete formula documentation for the Seertech Blueprint Studio
          </p>
          <Button
            variant="ghost" size="sm"
            onClick={() => setAllExpanded(prev => prev === undefined ? false : prev ? false : true)}
            className="text-[11px] text-slate-400 hover:text-[#0F2040] h-7 px-2 shrink-0 print:hidden"
            data-testid="expand-collapse-all-btn"
          >
            <ChevronsUpDown className="w-3.5 h-3.5 mr-1" />
            {allExpanded === false ? 'Expand All' : 'Collapse All'}
          </Button>
        </div>
      </div>

      {liveData && (
        <div className="mb-4 print:mb-3">
          <Badge className="bg-[#4ABFCF]/10 text-[#0F2040] border-[#4ABFCF]/20 text-xs" data-testid="live-values-badge">
            <Info className="w-3 h-3 mr-1" />
            Showing live values for: {est?.estimate_name}
          </Badge>
        </div>
      )}

      {/* ═══════════ CONTENT ═══════════ */}
      <div ref={contentRef} className="space-y-5">

        {/* ── 1. ESTIMATOR MATH ──────────────────────────────── */}
        <Section id="estimator" title="Estimator — Effort & Cost" icon={Calculator} forceOpen={allExpanded}>
          {/* 1a. Component Effort */}
          <div className="space-y-2">
            <SubHeading>1. Per-Component Effort</SubHeading>
            <Paragraph>Each selected component's effort is calculated by multiplying its base hours through a chain of multipliers.</Paragraph>
            <Formula lines={[
              'Component Effort =',
              '  Base Hours × Quantity × Use-Case Weight',
              '  × Composite Modifier × Implementation Multiplier',
            ]} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-500">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <span className="font-semibold text-[#0F2040]">Base Hours</span>
                <p>Default hours from the component library, varying by complexity (Simple / Standard / Complex). Can be overridden per-estimate or via Admin component settings.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <span className="font-semibold text-[#0F2040]">Use-Case Weight</span>
                <p>Implementation-type-specific multiplier: Corporate (corporate_weight), EXE/Ecommerce (exe_weight), or Workforce (workforce_weight). Defaults to 1.0.</p>
              </div>
            </div>
          </div>

          {/* 1b. Composite Modifier */}
          <div className="space-y-2">
            <SubHeading>2. Composite Modifier</SubHeading>
            <Paragraph>A single multiplier combining all 7 project factor sliders and the hosting modifier.</Paragraph>
            <Formula lines={[
              'Composite Modifier = Governance × Content Readiness',
              '  × Integration Complexity × Org Complexity',
              '  × Timeline Pressure × Client Capability',
              '  × Change Readiness × Hosting Modifier',
            ]} />
            <Paragraph>
              Each factor slider maps to a value between 0.8 (best) and 1.4 (worst). A neutral setting is 1.0.
              Hosting modifier is 1.0 for Public Cloud and 1.05 for Private Cloud.
            </Paragraph>
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-composite">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Values</div>
                <LiveValue label="Governance" value={mods.governance ?? 'n/a'} />
                <LiveValue label="Content Readiness" value={mods.content_readiness ?? 'n/a'} />
                <LiveValue label="Integration Complexity" value={mods.integration_complexity ?? 'n/a'} />
                <LiveValue label="Org Complexity" value={mods.org_complexity ?? 'n/a'} />
                <LiveValue label="Timeline Pressure" value={mods.timeline_pressure ?? 'n/a'} />
                <LiveValue label="Client Capability" value={mods.client_capability ?? 'n/a'} />
                <LiveValue label="Change Readiness" value={mods.change_readiness ?? 'n/a'} />
                <LiveValue label="Hosting Modifier" value={res.hosting_modifier || 1.0} />
                <LiveValue label="= Composite Modifier" value={res.composite_modifier} highlight />
              </div>
            )}
          </div>

          {/* 1c. Volume-Based Calculations */}
          <div className="space-y-2">
            <SubHeading>3. Volume-Based Components</SubHeading>
            <Paragraph>Some components support volume-based estimation instead of static base hours.</Paragraph>
            <Formula lines={[
              'Volume Effort = SUM( volume_count × rate_per_volume )',
              '  rate = complexity-specific (simple/standard/complex)',
              '  This replaces Base Hours in the main formula',
            ]} />
          </div>

          {/* 1d. Overhead */}
          <div className="space-y-2">
            <SubHeading>4. Overhead</SubHeading>
            <Paragraph>Two overhead categories are added on top of the summed component efforts:</Paragraph>
            <Formula lines={[
              'PM Overhead = Sum of Component Efforts × 20%',
              'Testing Overhead = Sum of Component Efforts × 15%',
              '',
              'Total Effort = Sum of Component Efforts + PM Overhead + Testing Overhead',
            ]} />
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-overhead">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Values</div>
                <LiveValue label="Component Effort Sum" value={`${Math.round(res.total_effort - (res.overhead?.pm_hours || 0) - (res.overhead?.testing_hours || 0))} hrs`} />
                <LiveValue label="PM Overhead (20%)" value={`${Math.round(res.overhead?.pm_hours || 0)} hrs`} />
                <LiveValue label="Testing Overhead (15%)" value={`${Math.round(res.overhead?.testing_hours || 0)} hrs`} />
                <LiveValue label="= Total Effort" value={`${res.total_effort?.toLocaleString()} hrs`} highlight />
              </div>
            )}
          </div>

          {/* 1e. Cost */}
          <div className="space-y-2">
            <SubHeading>5. Cost Calculation</SubHeading>
            <Formula lines={[
              'Component Cost = Component Final Effort × Role Hourly Rate',
              'PM Overhead Cost = PM Hours × PM Rate',
              'Testing Overhead Cost = Testing Hours × CF Rate',
              'Total Cost = Sum(Component Costs) + PM Overhead Cost + Testing Overhead Cost',
            ]} />
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-cost">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Cost by Role</div>
                {Object.entries(res.cost_by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, cost]) => (
                  <LiveValue key={role} label={`${ROLE_LABELS[role] || role} (${role})`} value={`$${Math.round(cost).toLocaleString()}`} />
                ))}
                <LiveValue label="PM Overhead" value={`$${Math.round(res.overhead?.pm_cost || 0).toLocaleString()}`} />
                <LiveValue label="Testing Overhead" value={`$${Math.round(res.overhead?.testing_cost || 0).toLocaleString()}`} />
                <LiveValue label="= Total Cost" value={`$${res.total_cost?.toLocaleString()}`} highlight />
              </div>
            )}
          </div>
        </Section>

        {/* ── 2. COMPLEXITY ────────────────────────────────────── */}
        <Section id="complexity" title="Complexity Score" icon={Info} forceOpen={allExpanded}>
          <div className="space-y-2">
            <Paragraph>A composite score (0–100) measuring implementation difficulty, derived from component selections and project factors.</Paragraph>
            <Formula lines={[
              'Complexity Score = min(Integrations × 10, 30)',
              '  + min(Extended Config × 5, 20)',
              '  + (Org Complexity slider - 0.8) × 50',
              '  + (Governance slider - 0.8) × 40',
              '  + Impl Type Bonus (Corporate=0, Workforce=10, EXE=15)',
              '',
              'Clamped to [0, 100]',
            ]} />
            <div className="overflow-hidden rounded-lg border border-slate-200 text-xs">
              <table className="w-full">
                <thead><tr className="bg-slate-50">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Range</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Label</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-3 py-1.5">0 – 29</td><td className="px-3 py-1.5 font-medium text-emerald-700">Simple</td></tr>
                  <tr><td className="px-3 py-1.5">30 – 59</td><td className="px-3 py-1.5 font-medium text-blue-700">Moderate</td></tr>
                  <tr><td className="px-3 py-1.5">60 – 79</td><td className="px-3 py-1.5 font-medium text-amber-700">Complex</td></tr>
                  <tr><td className="px-3 py-1.5">80 – 100</td><td className="px-3 py-1.5 font-medium text-red-700">Very Complex</td></tr>
                </tbody>
              </table>
            </div>
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-complexity">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Values</div>
                <LiveValue label="Score" value={res.complexity_score} />
                <LiveValue label="Label" value={res.complexity_label} highlight />
              </div>
            )}
          </div>
        </Section>

        {/* ── 3. CONFIDENCE & RANGE ────────────────────────────── */}
        <Section id="confidence" title="Confidence Score & Estimate Range" icon={Info} forceOpen={allExpanded}>
          <div className="space-y-2">
            <SubHeading>Confidence Score</SubHeading>
            <Paragraph>A directional model that measures estimate predictability based on project factor positions and complexity.</Paragraph>
            <Formula lines={[
              'Confidence = 90 (base)',
              '  + (1 - Governance) × 8',
              '  + (1 - Content Readiness) × 8',
              '  + (1 - Client Capability) × 8',
              '  + (1 - Change Readiness) × 12       ← highest weight',
              '  + (1 - Integration Complexity) × 10',
              '  + (1 - Org Complexity) × 8',
              '  + (1 - Timeline Pressure) × 6',
              '  - Complexity Score × 0.08',
              '',
              'Clamped to [50, 95]',
            ]} />
            <Paragraph>
              When all factors are neutral (1.0), the base confidence is ~90%. Improving readiness factors (below 1.0) increases confidence;
              worsening them (above 1.0) decreases it. Change Readiness has the highest weight (×12).
            </Paragraph>
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-confidence">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Calculation</div>
                <LiveValue label="Base" value="90" />
                <LiveValue label={`Governance (1 - ${mods.governance ?? 1}) × 8`} value={((1 - (mods.governance ?? 0.8)) * 8).toFixed(1)} />
                <LiveValue label={`Content (1 - ${mods.content_readiness ?? 1}) × 8`} value={((1 - (mods.content_readiness ?? 0.8)) * 8).toFixed(1)} />
                <LiveValue label={`Capability (1 - ${mods.client_capability ?? 1}) × 8`} value={((1 - (mods.client_capability ?? 0.8)) * 8).toFixed(1)} />
                <LiveValue label={`Change (1 - ${mods.change_readiness ?? 1}) × 12`} value={((1 - (mods.change_readiness ?? 0.8)) * 12).toFixed(1)} />
                <LiveValue label={`Integration (1 - ${mods.integration_complexity ?? 1}) × 10`} value={((1 - (mods.integration_complexity ?? 0.8)) * 10).toFixed(1)} />
                <LiveValue label={`Org (1 - ${mods.org_complexity ?? 1}) × 8`} value={((1 - (mods.org_complexity ?? 0.8)) * 8).toFixed(1)} />
                <LiveValue label={`Timeline (1 - ${mods.timeline_pressure ?? 1}) × 6`} value={((1 - (mods.timeline_pressure ?? 0.8)) * 6).toFixed(1)} />
                <LiveValue label={`Complexity penalty (${res.complexity_score} × 0.08)`} value={`-${(res.complexity_score * 0.08).toFixed(1)}`} />
                <LiveValue label="= Confidence" value={`${res.confidence}%`} highlight />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <SubHeading>Estimate Range</SubHeading>
            <Paragraph>The effort and cost range is derived from the confidence score.</Paragraph>
            <Formula lines={[
              'Variance = (100 - Confidence%) / 100',
              'Low  = Base × (1 - Variance × 0.5)',
              'High = Base × (1 + Variance × 0.5)',
            ]} />
            {res && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-range">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Range</div>
                <LiveValue label="Effort Range" value={`${res.effort_range?.low?.toLocaleString()} – ${res.effort_range?.high?.toLocaleString()} hrs`} />
                <LiveValue label="Cost Range" value={`$${res.cost_range?.low?.toLocaleString()} – $${res.cost_range?.high?.toLocaleString()}`} />
              </div>
            )}
          </div>
        </Section>

        {/* ── 4. PLANNER MATH ──────────────────────────────────── */}
        <Section id="planner" title="Planner — Timeline & Resource Allocation" icon={CalendarDays} forceOpen={allExpanded}>
          {/* 4a. Dynamic Duration */}
          <div className="space-y-2">
            <SubHeading>1. Dynamic Duration Calculation</SubHeading>
            <Paragraph>Project weeks are computed dynamically from a complexity baseline, adjusted by effort delta and project factors.</Paragraph>
            <Formula lines={[
              'Base Weeks = Complexity Preset (Simple=8, Standard=10, Complex=14)',
              '',
              'Effort Delta = Total Effort - Base Required Effort',
              'Increments = floor(Effort Delta / Increment Threshold)',
              '  default: +30 hrs per increment',
              'Effort Multiplier = (1 + Timing Increase %)^Increments',
              '  default: +10% per increment',
              '',
              'Factor Product = product of all project modifier values',
              '',
              'Timing Multiplier = Effort Multiplier × Factor Product',
              'Adjusted Weeks = round(Base Weeks × Timing Multiplier)',
            ]} />
            {tl?.timing_analysis && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-timing">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Timing</div>
                <LiveValue label="Complexity" value={tl.complexity} />
                <LiveValue label="Base Weeks" value={`${tl.timing_analysis.base_weeks}w`} />
                <LiveValue label="Base Required Effort" value={`${tl.timing_analysis.base_effort} hrs`} />
                <LiveValue label="Total Effort" value={`${tl.timing_analysis.total_effort} hrs`} />
                <LiveValue label="Effort Delta" value={`+${tl.timing_analysis.effort_delta} hrs`} />
                <LiveValue label="Increments" value={tl.timing_analysis.effort_increments} />
                <LiveValue label="Effort Multiplier" value={`×${tl.timing_analysis.effort_multiplier}`} />
                <LiveValue label="Factor Product" value={`×${tl.timing_analysis.factor_product}`} />
                <LiveValue label="Timing Multiplier" value={`×${tl.timing_analysis.timing_multiplier}`} />
                <LiveValue label="= Duration" value={`${tl.timing_analysis.adjusted_weeks} weeks`} highlight />
              </div>
            )}
          </div>

          {/* 4b. Overhead Stretch */}
          <div className="space-y-2">
            <SubHeading>2. Overhead Stretch</SubHeading>
            <Paragraph>When the timeline extends beyond the base distribution template, overhead roles scale proportionally.</Paragraph>
            <Formula lines={[
              'Stretch Ratio = Adjusted Weeks / Source Template Weeks',
              'If Stretch Ratio > 1.0:',
              '  Overhead Effort = Base Overhead × Stretch Ratio',
              '  Applies to: PM, Testing, and other overhead components',
            ]} />
          </div>

          {/* 4c. Resource Allocation */}
          <div className="space-y-2">
            <SubHeading>3. Resource Capacity & FTE</SubHeading>
            <Paragraph>Resource allocation converts effort hours to FTE requirements per week.</Paragraph>
            <Formula lines={[
              'Hours/Week = Hours/Day × Days/Week × (Utilization% / 100)',
              '',
              'Weekly FTE per Role = Week Hours for Role / Hours per Week',
              'Peak FTE = max(Total FTE across all weeks)',
              'Avg FTE = mean(Total FTE across all weeks)',
              '',
              'Weeks Needed per Role = ceil(Role Effort / (Hours/Week × FTE))',
            ]} />
            {tl && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-testid="live-resource">
                <div className="text-[10px] text-[#4ABFCF] font-semibold uppercase tracking-wide mb-1">Live Resource Assumptions</div>
                <LiveValue label="Hours/Day" value={tl.working_assumptions?.hours_per_day} />
                <LiveValue label="Days/Week" value={tl.working_assumptions?.days_per_week} />
                <LiveValue label="Utilization" value={`${tl.working_assumptions?.utilization_pct}%`} />
                <LiveValue label="Hours/Week per FTE" value={`${tl.working_assumptions?.hours_per_week} hrs`} />
                <LiveValue label="Peak FTE" value={tl.peak_fte} />
                <LiveValue label="Avg FTE" value={tl.avg_fte} />
                <LiveValue label="Total Duration" value={`${tl.total_duration_weeks} weeks`} highlight />
              </div>
            )}
          </div>

          {/* 4d. Private Cloud */}
          <div className="space-y-2">
            <SubHeading>4. Private Cloud Override</SubHeading>
            <Paragraph>When hosting model is Private Cloud, the Infrastructure (IF) role gets a forced allocation in Week 1.</Paragraph>
            <Formula lines={[
              'If hosting_model == "private_cloud":',
              '  Hosting Setup component → 32 hrs IF in Week 1',
              '  (overrides standard hosting hours)',
            ]} />
          </div>

          {/* 4e. Phase Distribution */}
          <div className="space-y-2">
            <SubHeading>5. Phase Distribution</SubHeading>
            <Paragraph>
              Each component has a pre-defined distribution template that spreads its effort across project weeks.
              Templates are complexity-specific (Simple/Standard/Complex) and are scaled proportionally when the
              target project weeks differ from the template's source weeks.
            </Paragraph>
            <Formula lines={[
              'For each component:',
              '  raw_dist = template distribution for component (source weeks)',
              '  scaled_dist = proportional scaling to target weeks',
              '  week_hours[w] += component_effort × scaled_dist[w]',
            ]} />
          </div>
        </Section>

        {/* ── 5. COMPARE MATH ──────────────────────────────────── */}
        <Section id="compare" title="Comparison Engine" icon={GitCompareArrows} forceOpen={allExpanded}>
          <div className="space-y-2">
            <SubHeading>1. Delta Calculations</SubHeading>
            <Paragraph>Both estimates are recalculated with current system settings (rate cards, component overrides) before comparison.</Paragraph>
            <Formula lines={[
              'Effort Delta = Compare Effort - Base Effort',
              'Cost Delta = Compare Cost - Base Cost',
              'Confidence Delta = Compare Confidence - Base Confidence',
              'Net Impact (%) = (Cost Delta / Base Cost) × 100',
            ]} />
            <Paragraph>
              Group-level deltas compare effort and cost for each component group (e.g., Core Configuration, Integrations).
              Component-level deltas show exactly which items drive the difference.
            </Paragraph>
          </div>
          <div className="space-y-2">
            <SubHeading>2. What-If Comparisons</SubHeading>
            <Paragraph>Cloned estimates allow What-If analysis: change modifiers, toggle components, or adjust effort.
              The clone is recalculated and compared against the original to quantify the impact of specific changes.</Paragraph>
            <Formula lines={[
              'What-If recalculates with:',
              '  - Modified modifiers (factor overrides)',
              '  - Excluded / added components',
              '  - Effort overrides per component',
              '  - Implementation type or hosting changes',
            ]} />
          </div>
        </Section>

        {/* ── 6. PLANNING CONFIG ───────────────────────────────── */}
        <Section id="config" title="Configurable Parameters (Super Admin)" icon={Info} defaultOpen={false} forceOpen={allExpanded !== undefined ? allExpanded : undefined}>
          <Paragraph>Super Admins can adjust these parameters via the Planning Configuration page. Changes affect all future calculations.</Paragraph>
          <div className="overflow-hidden rounded-lg border border-slate-200 text-xs">
            <table className="w-full">
              <thead><tr className="bg-slate-50">
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Parameter</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Default</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Used In</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="px-3 py-1.5">Complexity Week Presets</td><td className="px-3 py-1.5">Simple=8, Standard=10, Complex=14</td><td className="px-3 py-1.5">Planner duration</td></tr>
                <tr><td className="px-3 py-1.5">Effort Increment Hours</td><td className="px-3 py-1.5">30 hrs</td><td className="px-3 py-1.5">Dynamic timing</td></tr>
                <tr><td className="px-3 py-1.5">Timing Increase %</td><td className="px-3 py-1.5">10%</td><td className="px-3 py-1.5">Dynamic timing</td></tr>
                <tr><td className="px-3 py-1.5">Role Baseline Hours</td><td className="px-3 py-1.5">PM=80, CS=160, CF=160, etc.</td><td className="px-3 py-1.5">Planner</td></tr>
                <tr><td className="px-3 py-1.5">Complexity Multipliers</td><td className="px-3 py-1.5">Simple=0.6, Standard=1.0, Complex=1.8</td><td className="px-3 py-1.5">Planner</td></tr>
                <tr><td className="px-3 py-1.5">Impl Type Multipliers</td><td className="px-3 py-1.5">Corporate=1.0, EXE=1.15, Workforce=1.1</td><td className="px-3 py-1.5">Planner</td></tr>
                <tr><td className="px-3 py-1.5">Private Cloud IF Hours</td><td className="px-3 py-1.5">32 hrs</td><td className="px-3 py-1.5">Planner</td></tr>
                <tr><td className="px-3 py-1.5">Rate Card (per role)</td><td className="px-3 py-1.5">Admin-configurable</td><td className="px-3 py-1.5">Estimator cost</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ── 7. CONFIG CHANGE HISTORY ─────────────────────────── */}
        <Section id="changelog" title="Configuration Change History" icon={History} defaultOpen={false} forceOpen={allExpanded !== undefined ? allExpanded : undefined}>
          {!changelogOpen ? (
            <Button variant="outline" size="sm" onClick={() => setChangelogOpen(true)} data-testid="math-ref-load-changelog">
              <History className="w-3.5 h-3.5 mr-1.5" /> Load Change History
            </Button>
          ) : changelog.length === 0 ? (
            <Paragraph>No configuration changes recorded yet.</Paragraph>
          ) : (
            <div className="space-y-3">
              {changelog.map((entry, i) => (
                <div key={`changelog-${entry.timestamp || i}-${entry.user_email || ''}`} className="border-l-2 border-slate-200 pl-3 py-1" data-testid={`math-ref-changelog-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-[#0F2040]">{entry.user_name || entry.user_email}</span>
                    <span className="text-[10px] text-slate-400">
                      {new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' '}
                      {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {entry.changes.map((c, j) => (
                      <div key={`change-${c.field}-${j}`} className="text-[11px] text-slate-600 flex items-baseline gap-1.5 flex-wrap">
                        <span className="font-medium text-slate-700">{c.field.replace(/_/g, ' ')}:</span>
                        <span className="text-red-500/70 line-through">{typeof c.old === 'object' ? JSON.stringify(c.old) : String(c.old ?? '—')}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-600 font-medium">{typeof c.new === 'object' ? JSON.stringify(c.new) : String(c.new ?? '—')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Paragraph>Showing last 5 changes. Full history available on the Planning Configuration page.</Paragraph>
            </div>
          )}
        </Section>

      </div>

      {/* Print footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-slate-200 text-[9px] text-slate-400 text-center">
        Seertech Blueprint Studio — Calculation Reference — Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </div>
    </div>
  );
}
